import React, { useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Delete } from "@mui/icons-material";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { Icon } from "@iconify/react";

const csvConfig = mkConfig({
  fieldSeparator: ",",
  decimalSeparator: ".",
  useKeysAsHeaders: true,
});

const AdminExpandTable = ({
  columns,
  data,
  showRowAction = false,
  DataForExportAll,
  loading = false,
}) => {
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState("");

  if (!data) data = [];
  if (!DataForExportAll) DataForExportAll = data;

  const handleExportRows = (rows) => {
    const rowData = rows?.map((row) => row.original);
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(DataForExportAll);
    download(csvConfig)(csv);
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 2,
  };

  const styles = {
    iconButton: {
      width: "36px",
      height: "36px",
      border: "none",
      borderRadius: "50%",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      transition: "0.2s ease-in-out",
    },
    secondary: {
      backgroundColor: "#fce4ec",
      color: "#d32f2f",
    },
    secondaryHover: {
      backgroundColor: "#f8bbd0",
    },
  };

  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      isLoading: loading,
    },
    enableExpanding: true,
    getRowCanExpand: (row) => !!row.original.txn?.length,
    renderDetailPanel: ({ row }) => {
      const txn = row.original.txn;

      return (
        <Box sx={{ p: 0, backgroundColor: "lightgray" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Amount ($)</TableCell>
                <TableCell>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {txn.map((t, i) => (
                <TableRow
                  key={i}
                  sx={{
                    backgroundColor: t.summary === null ? "#bb2124" : "green",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setSelectedSummary(t.summary || "No summary available.");
                    setShowSummaryModal(true);
                  }}
                >
                  <TableCell sx={{ color: "white" }}>
                    {`DDR${String(t.bookingId).padStart(6, "0")}`}
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>
                    {t.s_firstName} {t.s_lastName}
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>{t.amount}</TableCell>
                  <TableCell sx={{ color: "white" }}>
                    <Tooltip title="View Summary" arrow>
                      <button
                        className="w-32-px h-32-px me-8 bg-success-focus rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                        style={{
                          cursor: "pointer",
                          backgroundColor: "white",
                        }}
                      >
                        <Icon
                          icon="mingcute:eye-line"
                          style={{ color: "#ffffff", fontSize: "20px" }}
                        />
                      </button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      );
    },
    initialState: { density: "compact" },
    enableRowSelection: false,
    columnFilterDisplayMode: "popover",
    paginationDisplayMode: "pages",
    positionToolbarAlertBanner: "bottom",
    enableRowActions: showRowAction,
    positionActionsColumn: "last",
    enableGlobalFilter: true,
    maxSize: 100,
    muiTableHeadCellProps: {
      sx: () => ({
        background: "#124178",
        fontWeight: "normal",
        color: "#ffffff",
      }),
    },
    renderRowActions: ({ row }) => (
      <Box display="flex" flexDirection="row" alignItems="center">
        <Tooltip title="Edit User">
          <IconButton>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton>
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      ></Box>
    ),
  });

  return (
    <>
      <MaterialReactTable table={table} />

      {/* Summary Modal */}
      <Modal
        open={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        aria-labelledby="summary-modal-title"
        aria-describedby="summary-modal-description"
      >
        <Box sx={{ ...modalStyle, position: "relative" }}>
          <button
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              ...styles.iconButton,
              ...styles.secondary,
            }}
            onClick={() => setShowSummaryModal(false)}
            title="Close"
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor =
                styles.secondaryHover.backgroundColor)
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor =
                styles.secondary.backgroundColor)
            }
          >
            <Icon icon="material-symbols:cancel-outline-rounded" />
          </button>
          <Typography id="summary-modal-title" variant="h6" mb={2}>
            Summary
          </Typography>

          {/* Scrollable content box */}
          <Box
            id="summary-modal-description"
            sx={{
              maxHeight: "300px",
              overflowY: "auto",
              paddingRight: "8px",
            }}
          >
            <Typography>{selectedSummary}</Typography>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AdminExpandTable;
